import {z} from 'zod';

type ValidationType = {
  callback: any;
  first_name: string;
  last_name: string;
  username: string;
  password: string;
  password2: string;
  email: string;
  setValidationErrorFields: (value: string[]) => void;
  setValidationErrorMessages: (value: Record<string, string>) => void;
  t: (key: string) => string;
};

// Strong password regex: at least 8 chars, 1 uppercase, 1 lowercase, 1 number
const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
const russianSymbolsRegex = /[а-яА-ЯЁё]/;

export const createRegistrationSchema = (t: (key: string) => string) => {
  return z
    .object({
      first_name: z.string().min(1, t('validation.required')),
      last_name: z.string().min(1, t('validation.required')),
      username: z
        .string()
        .min(5, t('validation.usernameMin'))
        .refine((val) => !val.includes(' '), t('validation.usernameNoSpaces'))
        .refine((val) => !val.includes('@'), t('validation.usernameNoAt'))
        .refine((val) => !russianSymbolsRegex.test(val), t('validation.usernameNoRussian')),
      email: z
        .string()
        .min(1, t('validation.required'))
        .email({message: t('validation.emailInvalid')}),
      password: z
        .string()
        .min(8, t('validation.passwordMin'))
        .refine((val) => !val.includes(' '), t('validation.passwordNoSpaces'))
        .refine((val) => !russianSymbolsRegex.test(val), t('validation.passwordNoRussian'))
        .refine((val) => strongPasswordRegex.test(val), t('validation.passwordWeak')),
      password2: z.string().min(1, t('validation.required')),
    })
    .refine((data) => data.password === data.password2, {
      message: t('validation.passwordMismatch'),
      path: ['password2'],
    });
};

export const validationFields = ({
  callback,
  first_name,
  last_name,
  username,
  password,
  password2,
  email,
  setValidationErrorFields,
  setValidationErrorMessages,
  t,
}: ValidationType): boolean => {
  const schema = createRegistrationSchema(t);

  try {
    schema.parse({
      first_name,
      last_name,
      username,
      email,
      password,
      password2,
    });

    setValidationErrorFields([]);
    setValidationErrorMessages({});
    return true;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const firstError = error.issues[0];
      const fieldPath = firstError.path[0] as string;

      callback(firstError.message, {variant: 'warning'});
      setValidationErrorFields([fieldPath]);
      setValidationErrorMessages({[fieldPath]: firstError.message});
      return false;
    }
    return false;
  }
};
